import Modal from "common/Modal/Modal";
import Button from "common/Buttons/Button";
import useCreatePayoutRequest from "features/affiliation/common/hooks/useCreatePayoutRequest";
import Text from "common/Typography/Text";
import Title from "common/Typography/Title";
import Input from "common/Inputs/Input";
import { toFixed } from "@shared/utils/numbers";
import { USD_PRECISION } from "features/affiliation/common/constants";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage";
import Select from "common/Select/Select";
import React, { ChangeEvent } from "react";
import styled, { css, useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import { hexToRGB } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, PayoutMethod } from "@shared/types";
import { formatUsd } from "features/affiliation/NewLook/newLookUtils";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface CreatePayoutModalProps {
  isVisible: boolean;
  closeModal: () => void;
  payoutMethods?: PayoutMethod[];
  availableFunds?: number;
}

const CreatePayoutModal = ({
  isVisible,
  closeModal,
  payoutMethods,
  availableFunds,
}: CreatePayoutModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { isNewLook } = useTheme();
  const {
    createPayoutHandler,
    createPayoutLoading,
    showAmountErrorMessage,
    getAmountErrorMessage,
    payoutAmount,
    setPayoutAmount,
    payoutMethodId,
    setPayoutMethodId,
    availableFundsWithPrecision,
    onCloseModal,
  } = useCreatePayoutRequest(payoutMethods, closeModal, availableFunds);

  const footer = [
    <Button key="cancel" onClick={onCloseModal}>
      {t("common.cancel")}
    </Button>,
    <Button
      key="create"
      type="primary"
      htmlType="submit"
      form="createPayoutForm"
      loading={createPayoutLoading}
      disabled={showAmountErrorMessage}
      onClick={createPayoutHandler}
    >
      {t("common.create")}
    </Button>,
  ];

  const content = (
    <>
      <AvailableBalance>
        <Text>{t("payouts.available_balance")}</Text>
        <Title level={4}>
          {isNewLook
            ? formatUsd(availableFunds)
            : `${availableFundsWithPrecision} ${t("payouts.usd")}`}
        </Title>
      </AvailableBalance>
      <FormItem>
        <Label>{t("payouts.payout_amount")}</Label>
        <Input
          suffix={t("payouts.usd")}
          defaultValue={toFixed(payoutAmount, USD_PRECISION)}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setPayoutAmount(Number(e.target.value))
          }
        />
        <RequiredFieldMessage
          requirement={showAmountErrorMessage}
          errorMessage={getAmountErrorMessage() || ""}
          style={{ marginTop: 2 }}
        />
      </FormItem>
      <FormItem>
        <Label>{t("payouts.payout_method")}</Label>
        <Select
          defaultValue={payoutMethodId}
          onChange={(methodId) => setPayoutMethodId(methodId)}
          style={{ width: "100%" }}
          mobileTitle={t("payouts.payout_method")}
          options={payoutMethods?.map((payoutMethod) => ({
            value: payoutMethod.payout_method_id,
            label: payoutMethod.name,
          }))}
        />
      </FormItem>
    </>
  );

  return (
    <Modal
      onCancel={onCloseModal}
      open={isVisible}
      title={t("payouts.create_modal_title")}
      footer={footer}
      destroyOnHidden={true}
    >
      {content}
    </Modal>
  );
};

export default CreatePayoutModal;

const AvailableBalance = styled(Flex)`
  background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.1)};
  margin-bottom: 16px;
  padding: 8px 12px;
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-bottom: 20px;
      padding: 10px 14px;
      border-radius: ${NEW_LOOK_RADIUS.control}px;
      background: ${getNewLookTokens(p.theme).well};
      box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
      /* A label and its figure, inset in a well (no tint). */
      > span {
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
      h4 {
        margin: 0;
        ${newLookType.figure};
        color: ${getNewLookTokens(p.theme).ink};
      }
    `}
`;
const Label = styled(Text)`
  margin-bottom: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      margin-bottom: 6px;
      ${newLookType.label};
      color: ${getNewLookTokens(p.theme).secondary};
    `}
`;
const FormItem = styled.div`
  margin-bottom: 16px;

  &:not(:last-child) {
    margin-bottom: 28px;
    height: 62px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Fields 16px apart; the last one ends at the modal's padding. */
      margin-bottom: 0;
      &:not(:last-child) {
        height: auto;
        margin-bottom: 16px;
      }
    `}
`;
